import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';
import { groupByMonth } from '@/utils/groupByMonth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DispatchEntry() {
  const [dispatches, setDispatches] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => user.role === 'admin' || entry.created_by === user.username;
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    part_id: '',
    customer: '',
    qty: '',
    invoice: ''
  });

  const fetchDispatches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/transactions/dispatch`, {
        
      });
      setDispatches(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to load dispatches');
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/masters/items`, {
        
      });
      setItems(response.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/masters/customers`, {
        
      });
      setCustomers(response.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  useEffect(() => {
    fetchDispatches();
    fetchItems();
    fetchCustomers();
  }, [fetchDispatches, fetchItems, fetchCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const data = {
        date: new Date(formData.date).toISOString(),
        part_id: formData.part_id,
        customer: formData.customer,
        qty: parseInt(formData.qty),
        invoice: formData.invoice,
        created_by: user.username
      };

      await axios.post(`${API}/transactions/dispatch`, data, {
        
      });
      toast.success('Dispatch created successfully');
      setShowForm(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        part_id: '',
        customer: '',
        qty: '',
        invoice: ''
      });
      fetchDispatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create dispatch');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this dispatch?')) return;
    try {
      await axios.delete(`${API}/transactions/dispatch/${id}`, {
        
      });
      toast.success('Dispatch deleted successfully');
      fetchDispatches();
    } catch (error) {
      toast.error('Failed to delete dispatch');
    }
  };

  const toggleMonth = (key) => setCollapsedMonths(prev => ({...prev, [key]: !prev[key]}));
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const monthGroups = groupByMonth(dispatches);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="dispatch-title">DISPATCH</h1>
          <p className="text-sm text-gray-700 mt-2">Record finished goods dispatch to customers</p>
        </div>
        <Button
          data-testid="toggle-dispatch-form-button"
          onClick={() => setShowForm(!showForm)}
          className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          {showForm ? 'CANCEL' : 'NEW DISPATCH'}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-border shadow-sm p-6">
          <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>NEW DISPATCH</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
              <Input
                data-testid="dispatch-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part ID</Label>
              <SearchableSelect
                testId="dispatch-part-id-select"
                className="mt-1.5"
                options={items.map(item => ({ value: item.part_id, label: `${item.part_id} - ${item.part_name}`, searchStr: `${item.part_id} ${item.part_name}` }))}
                value={formData.part_id}
                onValueChange={(value) => setFormData({...formData, part_id: value})}
                placeholder="Search part..."
                searchPlaceholder="Type to search parts..."
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Customer</Label>
              <Select
                value={formData.customer}
                onValueChange={(value) => setFormData({...formData, customer: value})}
                required
              >
                <SelectTrigger data-testid="dispatch-customer-select" className="mt-1.5 rounded-none">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(customer => (
                    <SelectItem key={customer.customer_id} value={customer.customer_id}>{customer.customer_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity</Label>
              <Input
                data-testid="dispatch-qty-input"
                type="number"
                value={formData.qty}
                onChange={(e) => setFormData({...formData, qty: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Invoice No</Label>
              <Input
                data-testid="dispatch-invoice-input"
                value={formData.invoice}
                onChange={(e) => setFormData({...formData, invoice: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div className="col-span-3 flex justify-end pt-4">
              <Button
                data-testid="submit-dispatch-button"
                type="submit"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                CREATE DISPATCH
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-border shadow-sm">
        <div className="border-b border-border p-4 bg-gray-50/50">
          <h2 className="text-xl font-semibold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>RECENT DISPATCHES</h2>
        </div>
        {dispatches.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No dispatches found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Customer</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Quantity</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Invoice</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              {monthGroups.map(group => (
                <tbody key={`group-${group.key}`}>
                  <tr className="bg-gray-100 cursor-pointer" onClick={() => toggleMonth(group.key)}>
                    <td colSpan={6} className="p-3">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                        {collapsedMonths[group.key] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        {group.label} ({group.entries.length} entries)
                      </div>
                    </td>
                  </tr>
                  {!collapsedMonths[group.key] && group.entries.map((dispatch, idx) => (
                    <tr key={dispatch.id || idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`dispatch-row-${idx}`}>
                      <td className="p-4 align-middle font-mono text-xs">{new Date(dispatch.date).toLocaleDateString()}</td>
                      <td className="p-4 align-middle font-mono text-xs">{dispatch.part_id}</td>
                      <td className="p-4 align-middle">{dispatch.customer}</td>
                      <td className="p-4 align-middle text-right font-mono text-xs">{dispatch.qty}</td>
                      <td className="p-4 align-middle font-mono text-xs">{dispatch.invoice}</td>
                      <td className="p-4 align-middle">
                        <div className="flex items-center justify-center">
                          {canModify(dispatch) && (
                            <button
                              data-testid={`delete-dispatch-${idx}`}
                              onClick={() => handleDelete(dispatch.id)}
                              className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}